'use client';

import { c as _c } from "react/compiler-runtime";
import { positionChartExternalTooltip } from '@/domains/pages/shared/chart-external-tooltip';
import { buildDimensionRadarTooltipHtml } from '@/domains/pages/ai-proficiency/components/chart-tooltip';
import { AIFI_CHART_WITHOUT_ANIMATION, METHODOLOGY_DIMENSIONS, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import type { LevelDimensionTargets } from '@/domains/pages/ai-proficiency/fixtures/upskilling-benchmarks';
import type { MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { radarPrecisePointHoverPlugin, radarSuppressTooltipDrawPlugin } from '@/domains/pages/ai-proficiency/components/radar-precise-hover';
import { UPSKILLING_TARGET_BORDER_DASH } from '@/domains/pages/ai-proficiency/components/upskilling-chart-styles';
import { AIFI_CHART_FONT_FAMILY } from '@/domains/pages/ai-proficiency/typography';
import { getWorstTwoDimensionKeys, rankDimensionsByLowestScore } from '@/domains/pages/ai-proficiency/utils/upskilling';
import { cn } from '@/utils/shad';
import { Chart as ChartJS, Filler, Legend, LineElement, PointElement, RadialLinearScale, RadarController, type ChartOptions } from 'chart.js';
import type { Context } from 'chartjs-plugin-datalabels';
import { useEffect, useMemo, useRef } from 'react';
import { Radar } from 'react-chartjs-2';
ChartJS.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Legend);
const RADAR_HOVER_PLUGINS = [radarPrecisePointHoverPlugin, radarSuppressTooltipDrawPlugin];
const AXIS_LABEL_DATASET_INDEX = 3;
const RADAR_SCALE_MAX = 121;
/** Place labels just outside the 100 ring, along each axis. */
const TIP_LABEL_RADIUS = 114;
const RADAR_AXIS_LABELS = METHODOLOGY_DIMENSIONS.map(dimension => dimension.shortLabel);
const MUTED_LABEL_COLOR = 'rgba(255, 255, 255, 0.45)';
const EMPHASIZED_POINT_DARKEN_FACTOR = 0.55;
const PROBLEMATIC_POINT_RADIUS = 4;
const PROBLEMATIC_POINT_HOVER_RADIUS = 5.5;
const DEFAULT_POINT_RADIUS = 2;
const DEFAULT_POINT_HOVER_RADIUS = 3;
function darkenHexColor(hex: string, factor = EMPHASIZED_POINT_DARKEN_FACTOR): string {
  if (!hex.startsWith('#') || hex.length !== 7) return hex;
  const clampChannel = (channel: number) => Math.max(0, Math.min(255, channel));
  const r = clampChannel(Math.round(parseInt(hex.slice(1, 3), 16) * factor));
  const g = clampChannel(Math.round(parseInt(hex.slice(3, 5), 16) * factor));
  const b = clampChannel(Math.round(parseInt(hex.slice(5, 7), 16) * factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
function isEmphasizedDimension(dimensionKey: string, worst: string | null, secondWorst: string | null): boolean {
  return dimensionKey === worst || dimensionKey === secondWorst;
}
type TooltipWithUpdate = {
  setActiveElements: (elements: {
    datasetIndex: number;
    index: number;
  }[], position: {
    x: number;
    y: number;
  }) => void;
  update: (mode?: boolean) => void;
};
type DimensionRadarChartProps = {
  level: AifiLevel;
  levelColor: string;
  groupDimensions: MethodologyDimension[];
  orgDimensions: MethodologyDimension[];
  levelTargets: LevelDimensionTargets;
  className?: string;
  suppressTooltip?: boolean;
  compact?: boolean;
};
function dimensionScoresByKey(dimensions: MethodologyDimension[]): Record<string, number> {
  return Object.fromEntries(dimensions.map(dimension => [dimension.key, dimension.score]));
}
function radarTooltipId(level: AifiLevel): string {
  return `upskilling-radar-tooltip-${level}`;
}
export function DimensionRadarChart(t0) {
  const $ = _c(97);
  const {
    level,
    levelColor,
    groupDimensions,
    orgDimensions,
    levelTargets,
    className,
    suppressTooltip: t1,
    compact: t2
  } = t0;
  const suppressTooltip = t1 === undefined ? false : t1;
  const compact = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== groupDimensions) {
    t3 = rankDimensionsByLowestScore(groupDimensions);
    $[0] = groupDimensions;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const ranked = t3;
  let t4;
  if ($[2] !== ranked) {
    t4 = getWorstTwoDimensionKeys(ranked);
    $[2] = ranked;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    worst,
    secondWorst
  } = t4;
  let groupData;
  let t5;
  if ($[4] !== groupDimensions || $[5] !== orgDimensions) {
    const groupByKey = dimensionScoresByKey(groupDimensions);
    const orgByKey = dimensionScoresByKey(orgDimensions);
    groupData = METHODOLOGY_DIMENSIONS.map(dimension => groupByKey[dimension.key] ?? 0);
    t5 = METHODOLOGY_DIMENSIONS.map(dimension_0 => orgByKey[dimension_0.key] ?? 0);
    $[4] = groupDimensions;
    $[5] = orgDimensions;
    $[6] = groupData;
    $[7] = t5;
  } else {
    groupData = $[6];
    t5 = $[7];
  }
  const orgData = t5;
  let t6;
  if ($[8] !== levelTargets) {
    t6 = METHODOLOGY_DIMENSIONS.map(dimension_1 => levelTargets[dimension_1.key] ?? 100);
    $[8] = levelTargets;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const targetData = t6;
  let t7;
  if ($[10] !== levelColor) {
    t7 = darkenHexColor(levelColor);
    $[10] = levelColor;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const emphasizedColor = t7;
  let t8;
  if ($[12] !== emphasizedColor || $[13] !== secondWorst || $[14] !== worst) {
    t8 = METHODOLOGY_DIMENSIONS.map(dimension_2 => {
      if (isEmphasizedDimension(dimension_2.key, worst, secondWorst)) {
        return emphasizedColor;
      }
      return MUTED_LABEL_COLOR;
    });
    $[12] = emphasizedColor;
    $[13] = secondWorst;
    $[14] = worst;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const pointLabelColors = t8;
  let t10;
  let t9;
  if ($[16] !== level) {
    t9 = () => {
      const tooltipId = radarTooltipId(level);
      return () => {
        document.getElementById(tooltipId)?.remove();
      };
    };
    t10 = [level];
    $[16] = level;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  const chartRef = useRef(null);
  let t11;
  let t12;
  if ($[19] !== level || $[20] !== suppressTooltip) {
    t11 = () => {
      if (!suppressTooltip) {
        return;
      }
      const tooltipId_0 = radarTooltipId(level);
      const tooltipEl = document.getElementById(tooltipId_0);
      if (tooltipEl) {
        tooltipEl.style.opacity = "0";
      }
      const chart = chartRef.current;
      if (!chart) {
        return;
      }
      chart.setActiveElements([]);
      const tooltip = chart.tooltip as TooltipWithUpdate | undefined;
      tooltip?.setActiveElements([], {
        x: 0,
        y: 0
      });
      tooltip?.update(true);
      chart.draw();
    };
    t12 = [level, suppressTooltip];
    $[19] = level;
    $[20] = suppressTooltip;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  if ($[23] !== emphasizedColor || $[24] !== levelColor || $[25] !== secondWorst || $[26] !== worst) {
    t13 = METHODOLOGY_DIMENSIONS.map(dimension_3 => isEmphasizedDimension(dimension_3.key, worst, secondWorst) ? emphasizedColor : levelColor);
    $[23] = emphasizedColor;
    $[24] = levelColor;
    $[25] = secondWorst;
    $[26] = worst;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const pointFillColors = t13;
  let t14;
  if ($[28] !== secondWorst || $[29] !== worst) {
    t14 = METHODOLOGY_DIMENSIONS.map(dimension_4 => isEmphasizedDimension(dimension_4.key, worst, secondWorst) ? PROBLEMATIC_POINT_RADIUS : DEFAULT_POINT_RADIUS);
    $[28] = secondWorst;
    $[29] = worst;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const pointRadii = t14;
  let t15;
  if ($[31] !== secondWorst || $[32] !== worst) {
    t15 = METHODOLOGY_DIMENSIONS.map(dimension_5 => isEmphasizedDimension(dimension_5.key, worst, secondWorst) ? PROBLEMATIC_POINT_HOVER_RADIUS : DEFAULT_POINT_HOVER_RADIUS);
    $[31] = secondWorst;
    $[32] = worst;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const pointHoverRadii = t15;
  const t16 = `${levelColor}33`;
  const t17 = `${levelColor}33`;
  let t18;
  if ($[34] !== groupData || $[35] !== level || $[36] !== levelColor || $[37] !== pointFillColors || $[38] !== pointHoverRadii || $[39] !== pointRadii || $[40] !== t16 || $[41] !== t17) {
    t18 = {
      label: level,
      data: groupData,
      borderColor: levelColor,
      backgroundColor: t16,
      borderWidth: 2,
      hoverBackgroundColor: t17,
      hoverBorderColor: levelColor,
      pointBackgroundColor: pointFillColors,
      pointBorderColor: pointFillColors,
      pointHoverBackgroundColor: pointFillColors,
      pointHoverBorderColor: pointFillColors,
      pointRadius: pointRadii,
      pointHoverRadius: pointHoverRadii,
      pointHitRadius: 0,
      pointBorderWidth: 0,
      pointHoverBorderWidth: 0,
      hoverBorderWidth: 2,
      fill: true
    };
    $[34] = groupData;
    $[35] = level;
    $[36] = levelColor;
    $[37] = pointFillColors;
    $[38] = pointHoverRadii;
    $[39] = pointRadii;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [4, 4];
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== orgData) {
    t20 = {
      label: "Org avg",
      data: orgData,
      borderColor: "#8A8A8A",
      backgroundColor: "transparent",
      borderWidth: 1.5,
      borderDash: t19,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false
    };
    $[44] = orgData;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = [...UPSKILLING_TARGET_BORDER_DASH];
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== levelColor || $[48] !== targetData) {
    t22 = {
      label: "Target",
      data: targetData,
      borderColor: levelColor,
      backgroundColor: "transparent",
      borderWidth: 1.5,
      borderDash: t21,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false
    };
    $[47] = levelColor;
    $[48] = targetData;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      label: "Axis labels",
      data: METHODOLOGY_DIMENSIONS.map(_temp),
      borderColor: "transparent",
      backgroundColor: "transparent",
      borderWidth: 0,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      showLine: false
    };
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t18 || $[52] !== t20 || $[53] !== t22) {
    t24 = {
      labels: RADAR_AXIS_LABELS,
      datasets: [t18, t20, t22, t23]
    };
    $[51] = t18;
    $[52] = t20;
    $[53] = t22;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const data = t24;
  let t25;
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      mode: "point",
      intersect: true,
      includeInvisible: false,
      axis: "r"
    };
    t26 = {
      mode: "point",
      intersect: true,
      includeInvisible: false,
      axis: "r"
    };
    $[55] = t25;
    $[56] = t26;
  } else {
    t25 = $[55];
    t26 = $[56];
  }
  let t27;
  if ($[57] !== suppressTooltip) {
    t27 = suppressTooltip ? [] : ["mousemove", "mouseout", "mouseleave", "click"];
    $[57] = suppressTooltip;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== compact) {
    t28 = {
      padding: compact ? {
        top: 10,
        right: 18,
        bottom: 10,
        left: 18
      } : {
        top: 18,
        right: 18,
        bottom: 18,
        left: 18
      }
    };
    $[59] = compact;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      display: false
    };
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== pointLabelColors) {
    t30 = ctx_0 => pointLabelColors[ctx_0.dataIndex] ?? MUTED_LABEL_COLOR;
    $[62] = pointLabelColors;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      size: 10,
      weight: 500,
      family: AIFI_CHART_FONT_FAMILY
    };
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t30) {
    t32 = {
      display: _temp2,
      clip: false,
      color: t30,
      font: t31,
      anchor: "center",
      align: "center",
      offset: 0,
      formatter: _temp3
    };
    $[65] = t30;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== groupData || $[68] !== level || $[69] !== levelColor || $[70] !== orgData || $[71] !== suppressTooltip || $[72] !== targetData) {
    t33 = {
      enabled: false,
      caretSize: 0,
      external(context) {
        const tooltipId_1 = radarTooltipId(level);
        let tooltipEl_0 = document.getElementById(tooltipId_1);
        if (!tooltipEl_0) {
          tooltipEl_0 = document.createElement("div");
          tooltipEl_0.id = tooltipId_1;
        }
        if (suppressTooltip) {
          tooltipEl_0.style.opacity = "0";
          return;
        }
        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
          tooltipEl_0.style.opacity = "0";
          return;
        }
        const dataPoint = tooltipModel.dataPoints?.[0];
        if (!dataPoint || dataPoint.datasetIndex !== 0) {
          tooltipEl_0.style.opacity = "0";
          return;
        }
        const index = dataPoint.dataIndex ?? 0;
        const dimension_6 = METHODOLOGY_DIMENSIONS[index];
        if (!dimension_6) {
          tooltipEl_0.style.opacity = "0";
          return;
        }
        tooltipEl_0.innerHTML = buildDimensionRadarTooltipHtml({
          dimensionLabel: dimension_6.label,
          dimensionDescription: dimension_6.description,
          levelLabel: level,
          levelColor,
          levelScore: groupData[index] ?? 0,
          orgAvg: orgData[index] ?? 0,
          target: targetData[index] ?? 100
        });
        positionChartExternalTooltip(context.chart, tooltipEl_0, tooltipModel);
      }
    };
    $[67] = groupData;
    $[68] = level;
    $[69] = levelColor;
    $[70] = orgData;
    $[71] = suppressTooltip;
    $[72] = targetData;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t32 || $[75] !== t33) {
    t34 = {
      legend: t29,
      datalabels: t32,
      tooltip: t33
    };
    $[74] = t32;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      display: false
    };
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      r: {
        min: 0,
        max: RADAR_SCALE_MAX,
        ticks: t35,
        afterBuildTicks: _temp4,
        grid: {
          color: "rgba(255, 255, 255, 0.12)"
        },
        angleLines: {
          color: "rgba(255, 255, 255, 0.08)"
        },
        pointLabels: {
          display: false
        }
      }
    };
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t27 || $[80] !== t28 || $[81] !== t34) {
    t37 = {
      responsive: true,
      maintainAspectRatio: false,
      clip: false,
      ...AIFI_CHART_WITHOUT_ANIMATION,
      interaction: t25,
      hover: t26,
      events: t27,
      layout: t28,
      plugins: t34,
      scales: t36
    };
    $[79] = t27;
    $[80] = t28;
    $[81] = t34;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  const options = t37;
  let t38;
  if ($[83] !== className) {
    t38 = cn("overflow-visible", className);
    $[83] = className;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  const t39 = `dimension-radar-${level}`;
  const t40 = compact ? "h-48" : "h-56";
  let t41;
  if ($[85] !== t40) {
    t41 = cn("relative w-full min-w-0 overflow-visible", t40);
    $[85] = t40;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] !== data || $[88] !== options) {
    t42 = <Radar ref={chartRef} data={data} options={options} plugins={RADAR_HOVER_PLUGINS} />;
    $[87] = data;
    $[88] = options;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] !== t41 || $[91] !== t42) {
    t43 = <div className={t41}>{t42}</div>;
    $[90] = t41;
    $[91] = t42;
    $[92] = t43;
  } else {
    t43 = $[92];
  }
  let t44;
  if ($[93] !== t38 || $[94] !== t39 || $[95] !== t43) {
    t44 = <div className={t38} data-testid={t39}>{t43}</div>;
    $[93] = t38;
    $[94] = t39;
    $[95] = t43;
    $[96] = t44;
  } else {
    t44 = $[96];
  }
  return t44;
}
function _temp4(scale) {
  scale.ticks = [{
    value: 25
  }, {
    value: 50
  }, {
    value: 75
  }, {
    value: 100
  }];
}
function _temp3(_value, ctx_1) {
  return METHODOLOGY_DIMENSIONS[ctx_1.dataIndex]?.shortLabel ?? "";
}
function _temp2(ctx) {
  return ctx.datasetIndex === AXIS_LABEL_DATASET_INDEX;
}
function _temp() {
  return TIP_LABEL_RADIUS;
}